import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { setCurrentUser } from '../../ducks/user.duck';
import { uploadBannerImage as uploadBannerImageAPI } from '../../util/api';
import { types as sdkTypes } from '../../util/sdkLoader';

const { UUID } = sdkTypes;

// ================ Async Thunks ================ //

//////////////////
// Upload Image //
//////////////////
export const uploadImageThunk = createAsyncThunk(
  'ProfileSettingsPage/uploadImage',
  ({ id, file }, { rejectWithValue, extra: sdk }) => {
    const bodyParams = {
      image: file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        return { id, uploadedImage };
      })
      .catch(e => {
        return rejectWithValue({ id, error: storableError(e) });
      });
  }
);
// Backward compatible wrapper for the uploadImage thunk
export const uploadImage = actionPayload => dispatch => {
  return dispatch(uploadImageThunk(actionPayload));
};

////////////////////
// Update Profile //
////////////////////
export const updateProfileThunk = createAsyncThunk(
  'ProfileSettingsPage/updateProfile',
  (actionPayload, { dispatch, rejectWithValue, extra: sdk }) => {
    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(setCurrentUser(currentUser));
        return response;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrapper for the updateProfile thunk
export const updateProfile = actionPayload => dispatch => {
  return dispatch(updateProfileThunk(actionPayload));
};

///////////////////
// Upload Banner //
///////////////////
export const uploadBannerImageThunk = createAsyncThunk(
  'ProfileSettingsPage/uploadBannerImage',
  ({ id, file }, { rejectWithValue }) => {
    const formData = new FormData();
    formData.append('file', file);

    return uploadBannerImageAPI(formData)
      .then(result => {
        const data = result.data || result;
        if (data?.imageUrl) {
          return { id, imageUrl: data.imageUrl };
        }
        throw new Error('Upload failed - no image URL returned');
      })
      .catch(e => {
        return rejectWithValue({ id, error: storableError(e) });
      });
  }
);
// Backward compatible wrapper
export const uploadBannerImage = actionPayload => dispatch => {
  return dispatch(uploadBannerImageThunk(actionPayload));
};

/////////////////////////////
// Update Featured Listings //
/////////////////////////////
const updateListingsFeaturedStatus = async (updates, sdk) => {
  for (const { id, isFeatured } of updates) {
    try {
      await sdk.ownListings.update(
        {
          id: new UUID(id),
          publicData: { isFeatured },
        },
        { expand: true }
      );
    } catch (error) {
      console.error(`Failed to update listing ${id}:`, error);
      // Continue with other listings even if one fails
    }
  }
};

export const updateFeaturedListingsThunk = createAsyncThunk(
  'ProfileSettingsPage/updateFeaturedListings',
  async ({ featuredListings, previousFeaturedListings = [] }, { dispatch, getState, rejectWithValue, extra: sdk }) => {
    try {
      const { currentUser } = getState().user;
      const currentPublicData = currentUser.attributes.profile.publicData || {};

      const userPayload = {
        publicData: {
          ...currentPublicData,
          featuredListings,
        },
      };

      const currentIds = featuredListings.map(fl => fl.listingId);
      const previousIds = previousFeaturedListings.map(fl => fl.listingId);
      const toAdd = currentIds.filter(id => !previousIds.includes(id));
      const toRemove = previousIds.filter(id => !currentIds.includes(id));

      const response = await sdk.currentUser.updateProfile(userPayload, {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      });

      const entities = denormalisedResponseEntities(response);
      const updatedUser = entities[0];
      dispatch(setCurrentUser(updatedUser));

      const listingUpdates = [
        ...toAdd.map(id => ({ id, isFeatured: 1 })),
        ...toRemove.map(id => ({ id, isFeatured: 0 })),
      ];

      await updateListingsFeaturedStatus(listingUpdates, sdk);
      return true;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);
// Backward compatible wrapper
export const updateFeaturedListings = (featuredListings, previousFeaturedListings = []) => dispatch => {
  return dispatch(updateFeaturedListingsThunk({ featuredListings, previousFeaturedListings }));
};

///////////////////
// Fetch Listings //
///////////////////
export const fetchListingsThunk = createAsyncThunk(
  'ProfileSettingsPage/fetchListings',
  (_, { rejectWithValue, extra: sdk }) => {
    const params = {
      perPage: 100,
      pub_isVariant: false,
      sort: 'pub_isFeatured',
      include: ['images'],
      'fields.image': ['variants.listing-card', 'variants.listing-card-2x'],
      'limit.images': 1,
    };

    return sdk.ownListings
      .query(params)
      .then(response => {
        const filteredData = response.data.data.filter(
          listing => !listing.attributes.publicData.isVariant
        );
        return filteredData;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrapper
export const fetchListings = () => dispatch => {
  return dispatch(fetchListingsThunk());
};

// ================ Slice ================ //

const profileSettingsPageSlice = createSlice({
  name: 'ProfileSettingsPage',
  initialState: {
    image: null,
    uploadImageError: null,
    uploadInProgress: false,
    bannerImage: null,
    uploadBannerError: null,
    uploadBannerInProgress: false,
    updateInProgress: false,
    updateProfileError: null,
    updatingFeaturedListings: false,
    updateFeaturedListingsError: null,
    listings: [],
    listingsInProgress: false,
    listingsError: null,
  },
  reducers: {
    clearUpdatedForm: state => {
      state.updateProfileError = null;
      state.uploadImageError = null;
      state.uploadBannerError = null;
    },
  },
  extraReducers: builder => {
    builder
      // uploadImage cases
      .addCase(uploadImageThunk.pending, (state, action) => {
        const { id, file } = action.meta.arg;
        state.image = { id, file };
        state.uploadInProgress = true;
        state.uploadImageError = null;
      })
      .addCase(uploadImageThunk.fulfilled, (state, action) => {
        const { id, uploadedImage } = action.payload;
        const { file } = state.image || {};
        state.image = { id, imageId: uploadedImage.id, file, uploadedImage };
        state.uploadInProgress = false;
      })
      .addCase(uploadImageThunk.rejected, (state, action) => {
        state.image = null;
        state.uploadInProgress = false;
        state.uploadImageError = action.payload?.error;
      })
      // updateProfile cases
      .addCase(updateProfileThunk.pending, state => {
        state.updateInProgress = true;
        state.updateProfileError = null;
      })
      .addCase(updateProfileThunk.fulfilled, state => {
        state.image = null;
        state.bannerImage = null;
        state.updateInProgress = false;
      })
      .addCase(updateProfileThunk.rejected, (state, action) => {
        state.image = null;
        state.updateInProgress = false;
        state.updateProfileError = action.payload;
      })
      // uploadBannerImage cases
      .addCase(uploadBannerImageThunk.pending, (state, action) => {
        const { id, file } = action.meta.arg;
        state.bannerImage = { id, file };
        state.uploadBannerInProgress = true;
        state.uploadBannerError = null;
      })
      .addCase(uploadBannerImageThunk.fulfilled, (state, action) => {
        const { id, imageUrl } = action.payload;
        const { file } = state.bannerImage || {};
        state.bannerImage = { id, imageUrl, file };
        state.uploadBannerInProgress = false;
      })
      .addCase(uploadBannerImageThunk.rejected, (state, action) => {
        state.bannerImage = null;
        state.uploadBannerInProgress = false;
        state.uploadBannerError = action.payload?.error;
      })
      // updateFeaturedListings cases
      .addCase(updateFeaturedListingsThunk.pending, state => {
        state.updatingFeaturedListings = true;
        state.updateFeaturedListingsError = null;
      })
      .addCase(updateFeaturedListingsThunk.fulfilled, state => {
        state.updatingFeaturedListings = false;
        state.updateFeaturedListingsError = null;
      })
      .addCase(updateFeaturedListingsThunk.rejected, (state, action) => {
        state.updatingFeaturedListings = false;
        state.updateFeaturedListingsError = action.payload;
      })
      // fetchListings cases
      .addCase(fetchListingsThunk.pending, state => {
        state.listingsInProgress = true;
        state.listingsError = null;
      })
      .addCase(fetchListingsThunk.fulfilled, (state, action) => {
        state.listings = action.payload;
        state.listingsInProgress = false;
        state.listingsError = null;
      })
      .addCase(fetchListingsThunk.rejected, (state, action) => {
        state.listingsInProgress = false;
        state.listingsError = action.payload;
      });
  },
});

export const { clearUpdatedForm } = profileSettingsPageSlice.actions;
export default profileSettingsPageSlice.reducer;
